/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Badge, Button } from '@repo/shared-client/components/ui'
import { Link as TanStackLink } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import type { EventsMenuQuery, EventsMenuQueryVariables } from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'

const EVENTS_MENU_QUERY: TypedDocumentNode<EventsMenuQuery, EventsMenuQueryVariables> = gql`
  query EventsMenuQuery {
    myPendingEventsCount
  }
`

type EventsMenuProps = {
  activeTab?: string
  categoriesRoute?: string
  listRoute?: string
}

const EventsMenu = ({
  activeTab = 'discover',
  categoriesRoute = '/event',
  listRoute = '/event/list',
}: EventsMenuProps) => {
  const { data, error, networkStatus } = useQuery<EventsMenuQuery, EventsMenuQueryVariables>(EVENTS_MENU_QUERY, {
    notifyOnNetworkStatusChange: true,
  })

  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()

  const isInitialLoading = networkStatus === NetworkStatus.loading
  const myPendingEventsCount = error
    ? -1
    : isInitialLoading || data?.myPendingEventsCount === null || data?.myPendingEventsCount === undefined
      ? 0
      : data.myPendingEventsCount

  const pendingBadgeLabel =
    myPendingEventsCount < 0 ? '!!' : myPendingEventsCount > 99 ? '99+' : String(myPendingEventsCount)

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <Button
        nativeButton={false}
        render={<TanStackLink to={categoriesRoute} />}
        variant={activeTab === 'categories' ? 'pill' : 'pill-outline'}
      >
        {t('Categories')}
      </Button>
      <Button
        nativeButton={false}
        render={<TanStackLink to={listRoute} />}
        variant={activeTab === 'discover' ? 'pill' : 'pill-outline'}
      >
        {t('Discover')}
      </Button>
      {isAuthenticated && (
        <>
          <Button
            nativeButton={false}
            render={<TanStackLink search={{ myEvents: true } as never} to={listRoute} />}
            variant={activeTab === 'myEvents' ? 'pill' : 'pill-outline'}
          >
            {t('My Events')}
          </Button>
          <Button
            nativeButton={false}
            render={<TanStackLink search={{ pendingEvents: true } as never} to={listRoute} />}
            variant={activeTab === 'pending' ? 'pill' : 'pill-outline'}
          >
            <span>{t('Invitations')}</span>
            {(myPendingEventsCount > 0 || myPendingEventsCount < 0) && (
              <Badge className="min-h-5 min-w-5 px-1" variant={activeTab === 'pending' ? 'secondary' : 'default'}>
                {pendingBadgeLabel}
              </Badge>
            )}
          </Button>
        </>
      )}
    </div>
  )
}

export default EventsMenu
